import { makeAutoObservable } from "mobx";
import { associationKey, } from "../core";
import { RArray } from "./RArray";
import { RMap } from "./RMap";
import { RSet } from "./RSet";
import { Sentry } from "../Sentry";
import { AddingDuplicateElementError } from "./AddingDuplicateElementError";
import { UnknownElementError } from "./UnknownElementError";
/*
 * Restaumatic Map wrapper including helper map operations. Uses `AssociationKey` as an association
 */
export class RMutableMap {
    constructor(params) {
        this.rawKeys = params.rawKeys;
        this.rawValues = params.rawValues;
        // NOTICE In most cases RMutableMap is used just as a throwaway structure for collecting items using getOrCreate
        // But in context of RMutableMultiSet is used in fully reactive context as a modifier item quantity storage
        if (params.makeAutoObservable) {
            // eslint-disable-next-line mobx/unconditional-make-observable
            makeAutoObservable(this);
        }
    }
    static empty(options) {
        return new RMutableMap({
            rawKeys: new Map(),
            rawValues: new Map(),
            makeAutoObservable: options.makeAutoObservable,
        });
    }
    static fromIterable(iterable, options) {
        var _a;
        const array = [...iterable];
        const rawKeys = new Map(array.map(([key]) => [associationKey(key), key]));
        const ignoreDuplicates = (_a = options.ignoreDuplicates) !== null && _a !== void 0 ? _a : false;
        if (!ignoreDuplicates && rawKeys.size !== array.length) {
            Sentry.logError(AddingDuplicateElementError.fromArray(array.map(([k, _]) => k)));
        }
        const rawValues = new Map(array.map(([key, value]) => [associationKey(key), value]));
        return new RMutableMap({
            rawKeys,
            rawValues,
            makeAutoObservable: options.makeAutoObservable,
        });
    }
    [Symbol.iterator]() {
        return this.entries[Symbol.iterator]();
    }
    get size() {
        return this.rawKeys.size;
    }
    get isEmpty() {
        return this.rawKeys.size === 0;
    }
    includes(key) {
        return this.rawKeys.has(associationKey(key));
    }
    rawFind(key) {
        var _a;
        return (_a = this.rawValues.get(key)) !== null && _a !== void 0 ? _a : null;
    }
    find(key) {
        return this.rawFind(associationKey(key));
    }
    rawGet(key) {
        const element = this.rawFind(key);
        if (element === null) {
            throw new UnknownElementError();
        }
        return element;
    }
    get(key) {
        return this.rawGet(associationKey(key));
    }
    get keys() {
        return RSet.fromIterable(this.rawKeys.values());
    }
    get values() {
        return new RArray([...this.rawValues.values()]);
    }
    get entries() {
        return new RArray([...this.rawKeys.entries()].map(([associationKey, key]) => [key, this.rawValues.get(associationKey)]));
    }
    get sample() {
        for (const value of this.rawValues.values()) {
            return value;
        }
        return null;
    }
    /*
     * Mutation operatoions
     */
    update(key, value) {
        if (!this.includes(key)) {
            throw new Error("Unable to update not existing element");
        }
        const ak = associationKey(key);
        this.rawValues.set(ak, value);
    }
    set(key, value) {
        if (this.includes(key)) {
            Sentry.logError(AddingDuplicateElementError.fromSingleton(key));
        }
        const ak = associationKey(key);
        this.rawKeys.set(ak, key);
        this.rawValues.set(ak, value);
    }
    getOrCreate(key, factory) {
        if (!this.includes(key)) {
            this.set(key, factory());
        }
        return this.get(key);
    }
    delete(key) {
        if (!this.includes(key)) {
            throw new UnknownElementError();
        }
        const ak = associationKey(key);
        this.rawKeys.delete(ak);
        this.rawValues.delete(ak);
    }
    clear() {
        this.rawKeys.clear();
        this.rawValues.clear();
    }
    freeze() {
        return RMap.fromIterable(this);
    }
}
